import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Radio,
  Alert,
  Tooltip,
  CircularProgress,
  Collapse,
  TextField,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CalendarViewWeekIcon from '@mui/icons-material/CalendarViewWeek';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { useMsal } from '@azure/msal-react';
import toast from 'react-hot-toast';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { initCalendarService, createDeadlineEvents } from '../../utils/microsoftCalendarService';
import { parseISODate } from '../../utils/weekUtils';
import {
  werkdagenLijst, telWerkdagen, plusWerkdagen, berekenPlanning, pinLimieten, korteDatum,
} from '../../utils/postproductiePlanning';

// Task type/status colors - based on Airtable statuses
const taskTypeColors = {
  // Blocking statuses (full day unavailable)
  'Draaidag': { bg: '#f97316', text: '#fff', blocking: true },
  'Vrij/intern': { bg: '#fbbf24', text: '#000', blocking: true },
  'Vrije dag': { bg: '#fbbf24', text: '#000', blocking: true },
  'Niet beschikbaar': { bg: '#6b7280', text: '#fff', blocking: true },

  // Work statuses
  'Montage': { bg: '#3b82f6', text: '#fff', blocking: false },
  'Animatie': { bg: '#ec4899', text: '#fff', blocking: false },
  'Preproductie': { bg: '#10b981', text: '#fff', blocking: false },
  'Regie': { bg: '#7c3aed', text: '#fff', blocking: false },
  'Edit': { bg: '#3b82f6', text: '#fff', blocking: false },

  // Priority/Other
  'Prio 1': { bg: '#dc2626', text: '#fff', blocking: false },
  'Prio 2': { bg: '#dc2626', text: '#fff', blocking: false },
  'Prio 3': { bg: '#f97316', text: '#fff', blocking: false },
  'Prio 4': { bg: '#ca8a04', text: '#fff', blocking: false },
  'Afspraak': { bg: '#9ca3af', text: '#fff', blocking: false },
  'Vanallesuurtje': { bg: '#60a5fa', text: '#fff', blocking: false },

  // Default
  'default': { bg: '#d1d5db', text: '#374151', blocking: false },
};

// Get task color and blocking type
const getTaskColor = (task) => {
  // Check status first
  if (task.status && taskTypeColors[task.status]) {
    return taskTypeColors[task.status];
  }
  // Check type
  if (task.type && taskTypeColors[task.type]) {
    return taskTypeColors[task.type];
  }
  // Check name for keywords
  const name = (task.name || '').toLowerCase();
  if (name.includes('draaidag') || name.includes('shoot')) {
    return taskTypeColors['Draaidag'];
  }
  if (name.includes('vrij') || name.includes('verlof') || name.includes('vakantie')) {
    return taskTypeColors['Vrije dag'];
  }
  if (name.includes('montage') || name.includes('edit')) {
    return taskTypeColors['Montage'];
  }
  if (name.includes('animatie') || name.includes('motion')) {
    return taskTypeColors['Animatie'];
  }
  return taskTypeColors['default'];
};

// Get blocking type for a task (for capacity display)
const getBlockingType = (task) => {
  const name = (task.name || '').toLowerCase();
  const status = (task.status || '').toLowerCase();

  // Check for draaidag
  if (status.includes('draaidag') || name.includes('draaidag') || name.includes('shoot')) {
    return 'draaidag';
  }
  // Check for vrij/verlof — op woordgrens: "vrijdag" of "vrijgave" is geen vrije dag.
  const vrij = /\bvrij\b|vrije dag|verlof|vakantie/;
  if (vrij.test(status) || vrij.test(name)) {
    return 'vrij';
  }
  return null;
};

// Bezetting per dag. Rustig: een lege of normale dag krijgt géén kleur, alleen
// een krappe (>80%) of overvolle (>100%) dag valt op. Voorheen was elke cel
// groen/geel/rood en zag je door de kleuren de planning niet meer.
// blockingType: 'draaidag', 'vrij', or null
const getCapacityStyle = (percentage, blockingType) => {
  if (blockingType === 'draaidag') {
    return { bg: '#ffedd5', text: '#9a3412', label: 'Draai' };
  }
  if (blockingType === 'vrij') {
    return { bg: '#f3f4f6', text: '#6b7280', label: 'Vrij' };
  }
  if (!percentage) {
    return { bg: 'transparent', text: '#d1d5db', label: '·' };
  }
  if (percentage <= 80) {
    return { bg: 'transparent', text: '#6b7280', label: `${percentage}%` };
  }
  if (percentage <= 100) {
    return { bg: '#fef9c3', text: '#854d0e', label: `${percentage}%` };
  }
  return { bg: '#fee2e2', text: '#b91c1c', label: `${percentage}%` };
};

// Role colors for new tasks bar
const roleColors = {
  'Editor': '#3b82f6',
  'Animator': '#ec4899',
  'Illustrator': '#f97316',
  'Projectleider': '#10b981',
  'Copywriter': '#8b5cf6',
  'Regisseur': '#7c3aed',
  'Creative': '#a855f7',
};

// Werkdagen als kolommen: vanaf de start, `aantal` werkdagen lang.
const DAGNAMEN = ['Zo', 'Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za'];
const MAANDEN = ['jan', 'feb', 'mrt', 'apr', 'mei', 'jun', 'jul', 'aug', 'sep', 'okt', 'nov', 'dec'];
const generateWorkingDays = (startIso, aantal) => {
  if (!startIso || aantal <= 0) return [];
  return werkdagenLijst(startIso, plusWerkdagen(startIso, aantal - 1)).map((iso, idx) => {
    const d = parseISODate(iso);
    return {
      date: d,
      iso,
      dayOfWeek: d.getDay(),
      dayName: DAGNAMEN[d.getDay()],
      dayNum: d.getDate(),
      month: MAANDEN[d.getMonth()],
      isFirstOfMonth: d.getDate() === 1 || idx === 0,
    };
  });
};

// Calculate hours per day for a task
const getTaskDaysMap = (task, days) => {
  const startIso = task.startDate;
  const endIso = task.endDate || task.startDate;
  const result = {};

  days.forEach(day => {
    if (day.iso >= startIso && day.iso <= endIso) {
      result[day.iso] = true;
    }
  });

  return result;
};

// Detect recurring appointments (weekly meetings, standups, etc.)
const isRecurringAppointment = (task) => {
  const name = (task.name || '').toLowerCase();
  const recurringKeywords = [
    'standup', 'stand-up', 'daily', 'weekly', 'wekelijks',
    'meeting', 'overleg', 'sync', 'check-in', 'checkin',
    'retro', 'retrospective', 'sprint', 'scrum',
    'teamoverleg', 'vergadering',
  ];
  return recurringKeywords.some(kw => name.includes(kw));
};

// Group tasks by type for display (e.g., all Vanallesuurtje on one line, small tasks grouped, recurring appointments)
const groupTasksForDisplay = (tasks, workingDays) => {
  const groupableTypes = ['vanallesuurtje'];
  const SMALL_TASK_THRESHOLD = 0.5; // Hours threshold for "small tasks"

  const grouped = [];
  const groupedTasks = {};
  const smallTasks = [];
  const recurringTasks = {};

  tasks.forEach(task => {
    const name = (task.name || '').toLowerCase();
    const hours = task.hours || 0;

    // Check if it's a groupable type (like Vanallesuurtje)
    const isGroupable = groupableTypes.some(type => name.includes(type));

    // Check if recurring appointment
    const isRecurring = isRecurringAppointment(task);

    // Check if it's a small task (less than threshold hours)
    const isSmallTask = hours > 0 && hours < SMALL_TASK_THRESHOLD;

    if (isGroupable) {
      const groupKey = groupableTypes.find(type => name.includes(type)) || 'other';
      if (!groupedTasks[groupKey]) {
        groupedTasks[groupKey] = {
          id: `group-${groupKey}`,
          name: groupKey.charAt(0).toUpperCase() + groupKey.slice(1),
          isGroup: true,
          tasks: [],
          status: task.status,
        };
      }
      groupedTasks[groupKey].tasks.push(task);
    } else if (isRecurring) {
      // Group recurring by normalized name (e.g., "weekly meeting" -> "meeting")
      const baseName = name.replace(/wekelijks|weekly|dagelijks|daily/gi, '').trim();
      const groupKey = baseName || 'recurring';
      if (!recurringTasks[groupKey]) {
        recurringTasks[groupKey] = {
          id: `group-recurring-${groupKey}`,
          name: `${task.name} (herhalend)`,
          isGroup: true,
          tasks: [],
          status: task.status || 'Afspraak',
        };
      }
      recurringTasks[groupKey].tasks.push(task);
    } else if (isSmallTask) {
      smallTasks.push(task);
    } else {
      grouped.push(task);
    }
  });

  // Add grouped tasks
  Object.values(groupedTasks).forEach(group => {
    grouped.push(group);
  });

  // Add recurring appointment groups
  Object.values(recurringTasks).forEach(group => {
    grouped.push(group);
  });

  // Add small tasks as a group if there are any
  if (smallTasks.length > 0) {
    grouped.push({
      id: 'group-small',
      name: `Kleine taken (${smallTasks.length}x)`,
      isGroup: true,
      tasks: smallTasks,
      status: '',
    });
  }

  return grouped;
};

const DAY_WIDTH = 44;

function PlanningPuzzleDialog({
  open,
  onClose,
  onApply,
  startDate,
  endDate,
  tasks = [],
  capacityData = {},
  personen = [],
  deliverableName = 'Deliverable',
  defaultFeedbackDays = 3,
  projectName = '',
  reviewDag = false,
  // Vaste dagen van de klant: [{ taakId, datum, soort, label }] — die taak moet dan klaar zijn.
  klantPins = [],
  // Beginstand: wat eerder in de puzzel is toegepast. De puzzel unmount bij het
  // verlaten van de stap; zonder beginstand toonde hij bij terugkomen de
  // standaardplanning, terwijl de wizard de aanpassingen wél naar de engine stuurt.
  beginDuren = {},
  beginOffsets = {},
  beginPlanningOffset = 0,
  beginToewijzingen = {},
  beginRollen = {},
}) {
  const { instance } = useMsal();
  const [calendarLoading, setCalendarLoading] = useState(false);

  // Selected person per role
  const [assignments, setAssignments] = useState(() => ({ ...beginRollen }));

  // Existing tasks per person
  const [existingTasks, setExistingTasks] = useState({});
  const [loadingTasks, setLoadingTasks] = useState(false);

  // Expanded persons (to see their tasks)
  const [expandedPersons, setExpandedPersons] = useState({});

  // Feedback days between versions (configurable)
  const [feedbackDays, setFeedbackDays] = useState(defaultFeedbackDays);

  // Planning offset (to shift the entire planning)
  const [planningOffset, setPlanningOffset] = useState(() => Math.max(0, beginPlanningOffset || 0));

  // Task duration overrides (for adjusting individual task lengths)
  const [taskDurationOverrides, setTaskDurationOverrides] = useState(() => ({ ...beginDuren }));

  // Per-task assignee overrides (taskId -> personId) — allows different person per version
  const [taskAssignments, setTaskAssignments] = useState(() => ({ ...beginToewijzingen }));

  // Drag state (resize or move)
  const dragRef = useRef(null); // { taskId, type: 'resize'|'move', startX, startDuration/startOffset }

  // Uitvoerder kiezen: een klikmenu. Het was een hover-tooltip met alle
  // editors, die over de halve planning heen viel zodra je met de muis langs kwam.
  const [uitvoerderMenu, setUitvoerderMenu] = useState(null); // { anchor, task }

  // Per-task offset overrides (taskId -> offset in days, for moving individual tasks)
  const [taskOffsets, setTaskOffsets] = useState(() => ({ ...beginOffsets }));

  // Per-task hours overrides (taskId -> hours, passed back via handleApply)
  const [taskHoursOverrides] = useState({});

  // Bij elk OPENEN de beginstand uit de wizard overnemen. Alleen bij mount
  // lezen was niet genoeg: wijzig je daarna in de wizard een uitvoerder, dan
  // zette de puzzel bij Toepassen de oude weer terug (Codex-hercheck).
  const wasOpen = useRef(open);
  useEffect(() => {
    if (open && !wasOpen.current) {
      setTaskDurationOverrides({ ...beginDuren });
      setTaskOffsets({ ...beginOffsets });
      setPlanningOffset(Math.max(0, beginPlanningOffset || 0));
      setTaskAssignments({ ...beginToewijzingen });
      setAssignments({ ...beginRollen });
      setFeedbackDays(defaultFeedbackDays);
    }
    wasOpen.current = open;
  // Alleen de open-overgang telt; de begin-props zijn elke render nieuwe objecten.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const HOURS_PER_DAY = 8;
  const startIso = String(startDate || '').slice(0, 10);
  const deadlineIso = String(endDate || '').slice(0, 10);
  // Werkdagen van start t/m deadline: dát is de periode (niet de uitloop erna).
  const periodeDagen = useMemo(() => telWerkdagen(startIso, deadlineIso), [startIso, deadlineIso]);

  // De nieuwe taken in kolommen (werkdag-index vanaf de start), live
  // herberekend met precies de rekensom van de engine: bij later beginnen of
  // een extra feedbackdag worden de taken korter, net als bij het opslaan.
  // Voorheen bleven de duren uit de wizard staan en zag je iets anders dan er
  // werd aangemaakt.
  const planning = useMemo(() => berekenPlanning(
    tasks.map((t) => ({ ...t, zwaarte: t.zwaarte ?? t.days ?? 1 })),
    {
      werkdagen: periodeDagen,
      feedbackDagen: feedbackDays,
      planningOffset,
      taakOffsets: taskOffsets,
      duurOverrides: taskDurationOverrides,
      reviewDag,
      pinLimieten: pinLimieten(klantPins, { startIso, feedbackDagen: feedbackDays, taakIds: tasks.map((t) => t.id) }),
    },
  ), [tasks, periodeDagen, feedbackDays, planningOffset, taskOffsets, taskDurationOverrides, reviewDag, klantPins, startIso]);
  // Klantdatums die niet te halen zijn — melden, niet stil verschuiven.
  const pinsTeLaat = useMemo(() => (klantPins || [])
    .filter((p) => planning.pinOverschrijdingen?.[p.taakId] > 0)
    .map((p) => ({ ...p, dagen: planning.pinOverschrijdingen[p.taakId], taak: tasks.find((t) => t.id === p.taakId)?.name || '' })),
  [klantPins, planning, tasks]);
  const planningPosities = planning.posities;
  const laatsteIdx = planning.laatsteIdx;

  // Tijdlijn: tot de deadline of het einde van de planning (wat later is), plus
  // een paar dagen lucht. Voorheen stond hij vast op deadline + 7 werkdagen, en
  // viel een taak die verder liep buiten beeld.
  const workingDays = useMemo(
    () => generateWorkingDays(startIso, Math.max(periodeDagen, laatsteIdx + 1) + 3),
    [startIso, periodeDagen, laatsteIdx],
  );
  const totalDays = workingDays.length;
  const laatsteZichtbaar = workingDays[totalDays - 1]?.iso || deadlineIso;

  // Laatste kolom die nog op/vóór de deadline valt.
  const deadlineIdx = periodeDagen - 1;

  // Balken in pixels, net als de dagkolommen. Eerst stonden ze in procenten van
  // de (veel bredere) rij: een taak van 2 dagen leek er 5 te beslaan en viel
  // niet samen met de datums erboven.
  const newTaskPositions = useMemo(() => planningPosities.map((p) => ({
    ...p,
    left: p.startIdx * DAY_WIDTH,
    width: Math.max(DAY_WIDTH * 0.6, p.duration * DAY_WIDTH - 4),
  })), [planningPosities]);

  // Loopt de planning voorbij de deadline?
  const deadlineOverschrijding = useMemo(() => {
    // deadlineIdx -1 = deadline vóór de start (bv. draaidag na de deadline): alles is te laat.
    if (!deadlineIso || laatsteIdx < 0 || laatsteIdx <= deadlineIdx) return null;
    return {
      dagen: laatsteIdx - deadlineIdx,
      eindDatum: workingDays[laatsteIdx]?.iso || null,
    };
  }, [laatsteIdx, deadlineIdx, workingDays, deadlineIso]);

  // Get unique roles from tasks
  const roles = useMemo(() => {
    const roleSet = new Set(tasks.map(t => t.type).filter(Boolean));
    return [...roleSet];
  }, [tasks]);

  // Get all person IDs from capacityData
  const allPersonIds = useMemo(() => {
    const ids = new Set();
    Object.values(capacityData).forEach(persons => {
      persons.forEach(p => ids.add(p.id));
    });
    return [...ids];
  }, [capacityData]);

  // Fetch existing tasks when dialog opens
  useEffect(() => {
    if (open && allPersonIds.length > 0 && startIso && laatsteZichtbaar) {
      fetchExistingTasks();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, allPersonIds, startIso, laatsteZichtbaar]);

  // Elke verschuiving vraagt een ander venster op. Alleen het antwoord op de
  // laatste vraag telt: een trager, ouder (korter) antwoord overschreef anders
  // de uitloop en liet bezette dagen vrij lijken.
  const laatsteVraag = useRef(0);
  const fetchExistingTasks = async () => {
    const vraag = ++laatsteVraag.current;
    setLoadingTasks(true);
    try {
      const params = new URLSearchParams();
      // Het hele zichtbare venster, ook de uitloop na de deadline — anders
      // stonden die dagen voor iedereen op 0% terwijl mensen daar wél bezet zijn.
      params.append('startDate', startIso);
      params.append('endDate', laatsteZichtbaar);
      allPersonIds.forEach(id => params.append('personIds[]', id));

      const response = await authFetch(`${BACKEND_URL}/Api/person-tasks-range?${params}`);
      if (response.ok) {
        const data = await response.json();
        if (vraag === laatsteVraag.current) setExistingTasks(data);
      }
    } catch (err) {
      console.error('Error fetching existing tasks:', err);
    } finally {
      if (vraag === laatsteVraag.current) setLoadingTasks(false);
    }
  };

  // Calculate capacity per day for each person
  const personCapacityPerDay = useMemo(() => {
    const result = {};

    allPersonIds.forEach(personId => {
      result[personId] = {};
      const personTasks = existingTasks[personId] || [];

      workingDays.forEach(day => {
        let totalHours = 0;
        let blockingType = null; // 'draaidag', 'vrij', or null
        const tasksOnDay = [];

        personTasks.forEach(task => {
          const taskDays = getTaskDaysMap(task, workingDays);
          if (taskDays[day.iso]) {
            // Uren over de HELE looptijd van de taak verdelen, niet alleen over
            // de dagen die toevallig in beeld zijn: een taak van 40 uur over twee
            // weken waarvan drie dagen zichtbaar waren, telde als 13 uur per dag.
            const taskWorkingDays = telWerkdagen(task.startDate, task.endDate || task.startDate)
              || Object.keys(taskDays).length;
            const hoursPerDay = (task.hours || 8) / Math.max(1, taskWorkingDays);
            totalHours += hoursPerDay;
            tasksOnDay.push(task);

            // Check if blocking (draaidag takes priority over vrij)
            const taskBlockingType = getBlockingType(task);
            if (taskBlockingType === 'draaidag') {
              blockingType = 'draaidag';
            } else if (taskBlockingType === 'vrij' && blockingType !== 'draaidag') {
              blockingType = 'vrij';
            }
          }
        });

        const percentage = Math.round((totalHours / HOURS_PER_DAY) * 100);
        result[personId][day.iso] = {
          hours: totalHours,
          percentage,
          blockingType,
          tasks: tasksOnDay,
        };
      });
    });

    return result;
  }, [allPersonIds, existingTasks, workingDays, HOURS_PER_DAY]);

  // Reset task duration to original
  const resetTaskDuration = (taskId) => {
    setTaskDurationOverrides(prev => {
      const newOverrides = { ...prev };
      delete newOverrides[taskId];
      return newOverrides;
    });
  };

  // Drag handlers for both resize and move
  const handleDragStart = useCallback((e, taskId, dragType) => {
    e.preventDefault();
    e.stopPropagation();
    const task = newTaskPositions.find(t => t.id === taskId);
    if (!task) return;

    dragRef.current = {
      taskId,
      type: dragType, // 'resize' or 'move'
      startX: e.clientX,
      startDuration: task.duration,
      startOffset: taskOffsets[taskId] || 0,
    };

    const handleMouseMove = (moveEvent) => {
      if (!dragRef.current) return;
      const { taskId, type, startDuration, startOffset } = dragRef.current;
      const deltaX = moveEvent.clientX - dragRef.current.startX;
      const deltaDays = Math.round(deltaX / DAY_WIDTH);

      // Een taak kan niet vóór het einde van de vorige (of vóór de start):
      // de engine schuift alleen naar achteren, dus het voorbeeld ook.
      if (type === 'resize') {
        const newDuration = Math.max(1, startDuration + deltaDays);
        setTaskDurationOverrides(prev => ({ ...prev, [taskId]: newDuration }));
      } else if (type === 'move') {
        const newOffset = Math.max(0, startOffset + deltaDays);
        setTaskOffsets(prev => ({ ...prev, [taskId]: newOffset }));
      } else if (type === 'resize-left') {
        const newOffset = Math.max(0, startOffset + deltaDays);
        const newDuration = Math.max(1, startDuration - (newOffset - startOffset));
        setTaskOffsets(prev => ({ ...prev, [taskId]: newOffset }));
        setTaskDurationOverrides(prev => ({ ...prev, [taskId]: newDuration }));
      }
    };

    const handleMouseUp = () => {
      dragRef.current = null;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = (dragType === 'resize' || dragType === 'resize-left') ? 'ew-resize' : 'grab';
    document.body.style.userSelect = 'none';
  }, [newTaskPositions, taskOffsets]);

  // Calculate which days each new task occupies, mapped per personId
  const newTaskDayMap = useMemo(() => {
    const mapByRole = {}; // { role: { dayIso: hours } } — legacy, for role-level display
    const mapByPerson = {}; // { personId: { dayIso: hours } } — for per-person display

    newTaskPositions.forEach(task => {
      if (task.isFeedbackGap) return;

      const role = task.type;
      if (!mapByRole[role]) mapByRole[role] = {};

      const totalTaskHours = task.hours > 0 ? task.hours : (task.duration * 8);
      const hoursPerDay = totalTaskHours / Math.max(1, task.duration);

      // Determine effective assignee for this task
      const assigneeId = taskAssignments[task.id] || assignments[role] || null;

      for (let i = 0; i < task.duration; i++) {
        const dayIdx = task.startIdx + i;
        if (dayIdx < workingDays.length) {
          const dayIso = workingDays[dayIdx]?.iso;
          if (dayIso) {
            mapByRole[role][dayIso] = (mapByRole[role][dayIso] || 0) + hoursPerDay;
            if (assigneeId) {
              if (!mapByPerson[assigneeId]) mapByPerson[assigneeId] = {};
              mapByPerson[assigneeId][dayIso] = (mapByPerson[assigneeId][dayIso] || 0) + hoursPerDay;
            }
          }
        }
      }
    });

    return { byRole: mapByRole, byPerson: mapByPerson };
  }, [newTaskPositions, workingDays, assignments, taskAssignments]);

  // Calculate combined capacity (existing + new) for selected persons
  const getCombinedCapacity = (personId, role, dayIso) => {
    const existingData = personCapacityPerDay[personId]?.[dayIso] || { hours: 0, blockingType: null };

    // Check if this person is assigned to ANY task (role-level or per-task)
    const isRoleSelected = assignments[role] === personId;
    const hasAnyTaskAssignment = Object.values(taskAssignments).includes(personId);
    const isSelected = isRoleSelected || hasAnyTaskAssignment;

    if (!isSelected) {
      return existingData;
    }

    // Add new task hours for this person (using per-person map)
    const newHours = newTaskDayMap.byPerson?.[personId]?.[dayIso] || 0;
    const combinedHours = existingData.hours + newHours;
    const percentage = Math.round((combinedHours / HOURS_PER_DAY) * 100);

    return {
      ...existingData,
      hours: combinedHours,
      percentage,
      newHours, // Track how much is new
      hasNewTasks: newHours > 0,
    };
  };

  // Calculate overall capacity percentage for a person (existing + new tasks assigned)
  const getOverallCapacity = (personId, role) => {
    // Sum existing hours over the period
    // Over de periode tot de deadline; de lege uitloop erna drukte het cijfer.
    const periode = workingDays.slice(0, Math.max(1, periodeDagen));
    const existingData = personCapacityPerDay[personId] || {};
    let existingHours = 0;
    periode.forEach(day => {
      existingHours += existingData[day.iso]?.hours || 0;
    });

    // Check if this person is assigned to any new tasks
    const isRoleSelected = assignments[role] === personId;
    const hasAnyTaskAssignment = Object.values(taskAssignments).includes(personId);
    const isSelected = isRoleSelected || hasAnyTaskAssignment;

    // Sum new task hours if selected
    let newHours = 0;
    if (isSelected) {
      const personNewTasks = newTaskDayMap.byPerson?.[personId] || {};
      periode.forEach(day => {
        newHours += personNewTasks[day.iso] || 0;
      });
    }

    const totalHours = existingHours + newHours;
    const totalAvailableHours = periode.length * HOURS_PER_DAY;
    return totalAvailableHours > 0 ? Math.round((totalHours / totalAvailableHours) * 100) : 0;
  };

  // Handle person selection (role-level default)
  const handleSelectPerson = (role, personId) => {
    setAssignments(prev => ({
      ...prev,
      [role]: prev[role] === personId ? null : personId,
    }));
  };

  // Get effective assignee for a task (per-task override or role default)
  // Returns '__UNASSIGNED__' if explicitly set to "nog te plannen", personId otherwise
  const getTaskAssignee = (task) => {
    if (taskAssignments[task.id] !== undefined) return taskAssignments[task.id];
    return assignments[task.type] || null;
  };

  // Toggle person expansion
  const togglePersonExpanded = (personId) => {
    setExpandedPersons(prev => ({
      ...prev,
      [personId]: !prev[personId],
    }));
  };

  // Handle apply - returns role-level assignments, per-task assignments, AND puzzle adjustments
  const handleAddToCalendar = async () => {
    const account = instance.getActiveAccount();
    if (!account) {
      toast.error('Log in met Microsoft om agenda-events aan te maken');
      return;
    }
    initCalendarService(instance, account);

    // Build deadline event from endDate + deliverableName
    const deadlineDate = endDate instanceof Date ? endDate.toISOString().split('T')[0] : String(endDate).split('T')[0];
    const deadlineEvents = [{
      name: deliverableName,
      deadline: deadlineDate,
      projectName: projectName || 'Project',
      deadlineType: 'Klant',
    }];

    setCalendarLoading(true);
    try {
      const result = await createDeadlineEvents(deadlineEvents);
      if (result.created.length > 0) {
        toast.success(`Deadline "${deliverableName}" in Outlook gezet (${deadlineDate} om 15:00)`);
      }
      if (result.errors.length > 0) {
        toast.error(`${result.errors.length} event(s) mislukt`);
      }
    } catch (err) {
      toast.error('Agenda-event aanmaken mislukt');
      console.error('[Calendar] Error:', err);
    } finally {
      setCalendarLoading(false);
    }
  };

  const handleApply = () => {
    const result = {};
    // Role-level assignments
    Object.entries(assignments).forEach(([role, personId]) => {
      if (personId && personId !== '__UNASSIGNED__') {
        const person = personen.find(p => p.id === personId);
        if (person) {
          result[role] = person;
        }
      }
    });
    // Per-task overrides (including "nog te plannen" = null)
    const taskOverrides = {};
    Object.entries(taskAssignments).forEach(([taskId, personId]) => {
      if (personId === '__UNASSIGNED__') {
        taskOverrides[taskId] = null; // Explicitly unassigned
      } else if (personId) {
        const person = personen.find(p => p.id === personId);
        if (person) {
          taskOverrides[taskId] = person;
        }
      }
    });
    result._taskAssignments = taskOverrides;

    // NEW: Pass puzzle adjustments (duration, offset, feedback, hours)
    result._taskDurationOverrides = taskDurationOverrides;
    result._taskOffsets = taskOffsets;
    result._feedbackDays = feedbackDays;
    result._planningOffset = planningOffset;
    result._taskHoursOverrides = taskHoursOverrides;

    onApply(result);
  };

  // Check if all tasks have assignments (either role-level or per-task)
  // Note: '__UNASSIGNED__' counts as "assigned" (explicitly marked for later planning)
  const allAssigned = tasks.every(t => {
    const assignee = getTaskAssignee(t);
    return assignee !== null && assignee !== undefined;
  });

  // Group new tasks by type for display on separate rows
  const tasksByType = useMemo(() => {
    const grouped = {};
    const feedbackGaps = [];

    newTaskPositions.forEach((task, idx) => {
      if (task.isFeedbackGap) {
        feedbackGaps.push(task);
        return;
      }
      const type = task.type || 'Overig';
      if (!grouped[type]) grouped[type] = [];
      grouped[type].push(task);
    });

    return { grouped, feedbackGaps };
  }, [newTaskPositions]);

  const dayWidth = DAY_WIDTH;
  const sidebarWidth = 176;
  const tijdlijnBreedte = totalDays * dayWidth;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 3, minHeight: 600, maxHeight: '90vh' }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <CalendarViewWeekIcon sx={{ color: '#3b82f6' }} />
          <Box>
            <Typography variant="h6" fontWeight={700}>
              Planning Puzzel
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {deliverableName}
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }} />
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
        {/* Periode + bediening: één rustige regel */}
        <Box sx={{ px: 3, py: 1.25, borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5 }}>
          <Typography variant="body2" color="text.secondary">
            <strong style={{ color: '#111827' }}>{korteDatum(startIso)}</strong> t/m deadline{' '}
            <strong style={{ color: '#111827' }}>{korteDatum(deadlineIso)}</strong>
            {' · '}{periodeDagen} werkdag{periodeDagen === 1 ? '' : 'en'}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
              <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>Begin</Typography>
              <IconButton
                size="small"
                aria-label="Eerder beginnen"
                onClick={() => setPlanningOffset(Math.max(0, planningOffset - 1))}
                disabled={planningOffset <= 0}
                sx={{ p: 0.25 }}
              >
                <ChevronLeftIcon fontSize="small" />
              </IconButton>
              <Typography variant="caption" sx={{ minWidth: 64, textAlign: 'center', fontWeight: 600 }}>
                {korteDatum(workingDays[planningOffset]?.iso || startIso)}
              </Typography>
              <IconButton
                size="small"
                aria-label="Later beginnen"
                onClick={() => setPlanningOffset(planningOffset + 1)}
                sx={{ p: 0.25 }}
              >
                <ChevronRightIcon fontSize="small" />
              </IconButton>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Typography variant="caption" color="text.secondary">Feedback per ronde</Typography>
              <TextField
                type="number"
                size="small"
                value={feedbackDays}
                onChange={(e) => setFeedbackDays(Math.max(1, Math.min(10, parseInt(e.target.value) || 1)))}
                inputProps={{ min: 1, max: 10, 'aria-label': 'Feedbackdagen per ronde', style: { width: 32, textAlign: 'center', padding: '4px' } }}
                sx={{ '& .MuiOutlinedInput-root': { height: 26 } }}
              />
              <Typography variant="caption" color="text.secondary">dagen</Typography>
            </Box>
          </Box>
        </Box>

        {pinsTeLaat.map((p) => (
          <Alert key={`${p.taakId}-${p.datum}`} severity="warning" sx={{ borderRadius: 0, py: 0.25 }} data-testid="puzzel-pin-te-laat">
            {p.taak} moet {korteDatum(p.datum)} klaar zijn ({p.label || 'klantdatum'}), maar er zijn {p.dagen} werkdag{p.dagen === 1 ? '' : 'en'} te weinig.
          </Alert>
        ))}

        {deadlineOverschrijding && (
          <Alert severity="warning" sx={{ borderRadius: 0, py: 0.25 }}>
            De planning loopt {deadlineOverschrijding.dagen} werkdag{deadlineOverschrijding.dagen === 1 ? '' : 'en'} over
            de deadline heen{deadlineOverschrijding.eindDatum ? ` (tot ${korteDatum(deadlineOverschrijding.eindDatum)})` : ''}.
            Maak taken korter of feedbackrondes kleiner.
          </Alert>
        )}

        {loadingTasks && (
          <Box sx={{ px: 3, py: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <CircularProgress size={16} />
            <Typography variant="caption" color="text.secondary">Bestaande taken laden…</Typography>
          </Box>
        )}

        {/* Main scrollable content */}
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {/* Vast bovenin: datums, de nieuwe planning en wie wat doet */}
          <Box sx={{
            position: 'sticky',
            top: 0,
            zIndex: 10,
            backgroundColor: '#fff',
            borderBottom: '1px solid #e5e7eb',
            width: 'max-content',
            minWidth: '100%',
          }}>
          {/* Datums */}
          <Box sx={{ display: 'flex', borderBottom: '1px solid #e5e7eb' }}>
            <Box sx={{ width: sidebarWidth, minWidth: sidebarWidth, position: 'sticky', left: 0, zIndex: 2, backgroundColor: '#fff', borderRight: '1px solid #e5e7eb', px: 1.5, display: 'flex', alignItems: 'flex-end', pb: 0.5 }}>
              <Typography variant="caption" color="text.secondary">
                {workingDays[0] ? `${workingDays[0].month}` : ''}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', width: tijdlijnBreedte }}>
              {workingDays.map((day, idx) => (
                <Box
                  key={day.iso}
                  sx={{
                    width: dayWidth,
                    minWidth: dayWidth,
                    textAlign: 'center',
                    py: 0.5,
                    // Maandag = nieuwe week: een dun streepje, verder geen lijnen.
                    borderLeft: idx === deadlineIdx + 1
                      ? '2px solid #ef4444'
                      : (day.dayOfWeek === 1 && idx > 0 ? '1px solid #e5e7eb' : '1px solid transparent'),
                    color: idx > deadlineIdx ? '#9ca3af' : 'inherit',
                  }}
                >
                  <Typography variant="caption" display="block" sx={{ fontSize: '0.6rem', color: 'inherit', opacity: 0.8 }}>
                    {day.dayName}
                  </Typography>
                  <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.75rem', color: 'inherit' }}>
                    {day.dayNum}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>

          {/* De nieuwe taken, één rij per rol */}
          {Object.entries(tasksByType.grouped).map(([type, typeTasks], rijIdx) => (
            <Box key={type} sx={{ display: 'flex' }}>
              <Box sx={{
                width: sidebarWidth,
                minWidth: sidebarWidth,
                position: 'sticky',
                left: 0,
                zIndex: 2,
                backgroundColor: '#fff',
                borderRight: '1px solid #e5e7eb',
                px: 1.5,
                display: 'flex',
                alignItems: 'center',
              }}>
                <Typography variant="caption" fontWeight={700} sx={{ color: '#111827' }}>
                  Nieuw · {type}
                </Typography>
              </Box>
              <Box sx={{ width: tijdlijnBreedte, position: 'relative', height: 40 }}>
                {/* Na de deadline licht gearceerd */}
                {deadlineIdx + 1 < totalDays && (
                  <Box sx={{ position: 'absolute', top: 0, bottom: 0, left: (deadlineIdx + 1) * dayWidth, right: 0, backgroundColor: '#fafafa', borderLeft: '2px solid #ef4444' }} />
                )}
                {typeTasks.map((task, idx) => {
                  const isOverridden = taskDurationOverrides[task.id] !== undefined;
                  const isMoved = (taskOffsets[task.id] || 0) !== 0;
                  const aangepast = isOverridden || isMoved;

                  return (
                    <Tooltip
                      key={task.id || idx}
                      title={
                        <Box>
                          <Typography variant="caption" display="block" fontWeight={600}>{task.name}</Typography>
                          <Typography variant="caption" display="block">
                            {korteDatum(workingDays[task.startIdx]?.iso)} – {korteDatum(workingDays[task.startIdx + task.duration - 1]?.iso)}
                            {' · '}{task.duration} {task.duration === 1 ? 'dag' : 'dagen'}{task.hours ? ` · ${task.hours} uur` : ''}
                          </Typography>
                          <Typography variant="caption" display="block" sx={{ mt: 0.5, opacity: 0.75 }}>
                            Sleep om te verschuiven, sleep een rand om de duur te wijzigen{aangepast ? ' · dubbelklik = terugzetten' : ''}
                          </Typography>
                        </Box>
                      }
                      arrow
                    >
                      <Box
                        data-testid="puzzel-balk"
                        data-start={task.startIdx}
                        data-duur={task.duration}
                        onMouseDown={(e) => handleDragStart(e, task.id, 'move')}
                        onDoubleClick={() => {
                          if (isOverridden) resetTaskDuration(task.id);
                          if (isMoved) setTaskOffsets(prev => { const n = { ...prev }; delete n[task.id]; return n; });
                        }}
                        sx={{
                          position: 'absolute',
                          left: task.left + 2,
                          width: task.width,
                          top: 6,
                          height: 28,
                          backgroundColor: roleColors[task.type] || '#3b82f6',
                          borderRadius: 1,
                          display: 'flex',
                          alignItems: 'center',
                          px: 1,
                          outline: aangepast ? '2px solid #f59e0b' : 'none',
                          outlineOffset: 1,
                          cursor: 'grab',
                          '&:active': { cursor: 'grabbing' },
                        }}
                      >
                        <Typography
                          variant="caption"
                          noWrap
                          sx={{ color: '#fff', fontWeight: 600, fontSize: '0.65rem', pointerEvents: 'none' }}
                        >
                          {task.name}
                        </Typography>

                        {/* Randen om de duur te slepen */}
                        <Box
                          onMouseDown={(e) => { e.stopPropagation(); handleDragStart(e, task.id, 'resize-left'); }}
                          sx={{ position: 'absolute', left: -3, top: 0, bottom: 0, width: 8, cursor: 'ew-resize' }}
                        />
                        <Box
                          data-testid="puzzel-rechterrand"
                          onMouseDown={(e) => { e.stopPropagation(); handleDragStart(e, task.id, 'resize'); }}
                          sx={{ position: 'absolute', right: -3, top: 0, bottom: 0, width: 8, cursor: 'ew-resize' }}
                        />
                      </Box>
                    </Tooltip>
                  );
                })}
                {/* Feedbackrondes: alleen op de eerste rij, als stippellijn */}
                {rijIdx === 0 && tasksByType.feedbackGaps.map((gap, idx) => (
                  <Tooltip key={gap.id || `fb-${idx}`} title={`Feedback van de klant (${gap.duration} ${gap.duration === 1 ? 'dag' : 'dagen'})`} arrow>
                    <Box
                      sx={{
                        position: 'absolute',
                        left: gap.left + 2,
                        width: gap.width,
                        top: 12,
                        height: 16,
                        border: '1px dashed #cbd5e1',
                        borderRadius: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Typography variant="caption" sx={{ color: '#94a3b8', fontSize: '0.6rem' }}>
                        feedback
                      </Typography>
                    </Box>
                  </Tooltip>
                ))}
              </Box>
            </Box>
          ))}

          {/* Wie doet wat: per taak een naam onder de balk; klik = kiezen */}
          <Box sx={{ display: 'flex' }}>
            <Box sx={{
              width: sidebarWidth,
              minWidth: sidebarWidth,
              position: 'sticky',
              left: 0,
              zIndex: 2,
              backgroundColor: '#fff',
              borderRight: '1px solid #e5e7eb',
              px: 1.5,
              display: 'flex',
              alignItems: 'center',
            }}>
              <Typography variant="caption" color="text.secondary">Uitvoerder</Typography>
            </Box>
            <Box sx={{ width: tijdlijnBreedte, position: 'relative', height: 28 }}>
              {newTaskPositions.filter(t => !t.isFeedbackGap).map((task) => {
                const assigneeId = getTaskAssignee(task);
                const hasTaskOverride = taskAssignments[task.id] !== undefined;
                const isUnassigned = assigneeId === '__UNASSIGNED__';
                const assigneePerson = (assigneeId && !isUnassigned)
                  ? ((capacityData[task.type] || []).find(p => p.id === assigneeId) || personen.find(p => p.id === assigneeId))
                  : null;
                const naam = assigneePerson
                  ? ((assigneePerson.firstName || assigneePerson.fields?.['Volledige naam'] || assigneePerson.name || '').split(' ')[0])
                  : (isUnassigned ? 'nog te plannen' : 'kies…');

                return (
                  <Box
                    key={`assign-${task.id}`}
                    component="button"
                    type="button"
                    onClick={(e) => setUitvoerderMenu({ anchor: e.currentTarget, task })}
                    aria-label={`Uitvoerder voor ${task.name}`}
                    sx={{
                      position: 'absolute',
                      left: task.left + 2,
                      width: task.width,
                      top: 4,
                      height: 20,
                      border: 'none',
                      background: 'none',
                      p: 0,
                      px: 0.5,
                      cursor: 'pointer',
                      textAlign: 'left',
                      font: 'inherit',
                      fontSize: '0.7rem',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      color: assigneePerson ? '#111827' : '#9ca3af',
                      fontWeight: hasTaskOverride ? 600 : 400,
                      fontStyle: assigneePerson ? 'normal' : 'italic',
                      borderRadius: 0.5,
                      '&:hover': { backgroundColor: '#f3f4f6' },
                    }}
                  >
                    {naam}
                  </Box>
                );
              })}
            </Box>
          </Box>
          </Box>
          {/* End sticky planning section */}

          <Menu
            anchorEl={uitvoerderMenu?.anchor || null}
            open={!!uitvoerderMenu}
            onClose={() => setUitvoerderMenu(null)}
            PaperProps={{ sx: { maxHeight: 360, minWidth: 200 } }}
          >
            {uitvoerderMenu && (() => {
              const task = uitvoerderMenu.task;
              const huidig = getTaskAssignee(task);
              const kies = (waarde) => {
                if (waarde === null) {
                  // Terug naar wie voor de hele rol gekozen is.
                  setTaskAssignments(prev => { const n = { ...prev }; delete n[task.id]; return n; });
                } else {
                  setTaskAssignments(prev => ({ ...prev, [task.id]: waarde }));
                }
                setUitvoerderMenu(null);
              };
              return [
                <MenuItem key="kop" disabled dense sx={{ opacity: '1 !important' }}>
                  <Typography variant="caption" color="text.secondary">{task.name}</Typography>
                </MenuItem>,
                ...(capacityData[task.type] || []).map((p) => (
                  <MenuItem key={p.id} dense selected={huidig === p.id} onClick={() => kies(p.id)}>
                    <ListItemText primaryTypographyProps={{ variant: 'body2' }}>{p.firstName || p.name}</ListItemText>
                  </MenuItem>
                )),
                <MenuItem key="later" dense selected={huidig === '__UNASSIGNED__'} onClick={() => kies('__UNASSIGNED__')}>
                  <ListItemText primaryTypographyProps={{ variant: 'body2', fontStyle: 'italic', color: 'text.secondary' }}>Nog te plannen</ListItemText>
                </MenuItem>,
                taskAssignments[task.id] !== undefined && (
                  <MenuItem key="rol" dense onClick={() => kies(null)}>
                    <ListItemIcon><RestartAltIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primaryTypographyProps={{ variant: 'body2' }}>Zelfde als de rol</ListItemText>
                  </MenuItem>
                ),
              ].filter(Boolean);
            })()}
          </Menu>

          {/* Roles and persons */}
          {roles.map(role => {
            const roleCapacity = capacityData[role] || [];
            const selectedPersonId = assignments[role];

            return (
              <Box key={role}>
                {/* Rolkop: wie kan dit doen, en hoe vol zitten ze al */}
                <Box sx={{
                  display: 'flex',
                  backgroundColor: '#fff',
                  borderBottom: '1px solid #e5e7eb',
                  width: 'max-content',
                  minWidth: '100%',
                }}>
                  <Box sx={{
                    width: sidebarWidth,
                    minWidth: sidebarWidth,
                    position: 'sticky', left: 0, zIndex: 1, backgroundColor: 'inherit',
                    px: 1.5,
                    pt: 2,
                    pb: 0.5,
                  }}>
                    <Typography variant="caption" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.4, color: '#6b7280' }}>
                      {role}s — kies wie
                    </Typography>
                  </Box>
                </Box>

                {/* Persons for this role */}
                {roleCapacity.length > 0 ? (
                  roleCapacity.map(person => {
                    const isSelected = selectedPersonId === person.id;
                    // Calculate overall capacity dynamically (includes new tasks if selected)
                    const overallPercentage = getOverallCapacity(person.id, role);
                    const personDayCapacity = personCapacityPerDay[person.id] || {};
                    const personTasks = existingTasks[person.id] || [];
                    const isExpanded = expandedPersons[person.id] || false;

                    return (
                      <Box key={person.id}>
                        {/* Person row with percentages per day */}
                        <Box
                          sx={{
                            display: 'flex',
                            width: 'max-content',
                            minWidth: '100%',
                            borderBottom: '1px solid #f3f4f6',
                            backgroundColor: isSelected ? '#eff6ff' : '#fff',
                            '&:hover': { backgroundColor: isSelected ? '#eff6ff' : '#f9fafb' },
                          }}
                        >
                          {/* Person info sidebar */}
                          <Box
                            sx={{
                              width: sidebarWidth,
                              minWidth: sidebarWidth,
                              position: 'sticky', left: 0, zIndex: 1, backgroundColor: 'inherit',
                              borderRight: '1px solid #e5e7eb',
                              p: 0.5,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.25,
                            }}
                          >
                            {/* Expand arrow */}
                            <IconButton
                              size="small"
                              onClick={() => togglePersonExpanded(person.id)}
                              sx={{ p: 0.25 }}
                            >
                              {isExpanded ? (
                                <KeyboardArrowDownIcon sx={{ fontSize: 18 }} />
                              ) : (
                                <KeyboardArrowRightIcon sx={{ fontSize: 18 }} />
                              )}
                            </IconButton>

                            {/* Radio button */}
                            <Radio
                              checked={isSelected}
                              size="small"
                              sx={{ p: 0.25 }}
                              onClick={() => handleSelectPerson(role, person.id)}
                            />

                            {/* Name */}
                            <Typography
                              variant="body2"
                              fontWeight={isSelected ? 600 : 400}
                              noWrap
                              sx={{ flex: 1, fontSize: '0.75rem', cursor: 'pointer' }}
                              onClick={() => handleSelectPerson(role, person.id)}
                            >
                              {person.firstName || person.name}
                            </Typography>

                            {/* Bezetting over de periode: alleen kleur als het krap wordt */}
                            <Tooltip title="Bezetting tot de deadline, inclusief deze planning als hij/zij gekozen is" arrow>
                              <Typography
                                variant="caption"
                                sx={{
                                  minWidth: 34,
                                  textAlign: 'right',
                                  pr: 0.5,
                                  fontWeight: 600,
                                  color: overallPercentage > 100 ? '#b91c1c' : overallPercentage > 80 ? '#a16207' : '#6b7280',
                                }}
                              >
                                {overallPercentage}%
                              </Typography>
                            </Tooltip>
                          </Box>

                          {/* Day cells with percentages */}
                          <Box sx={{ display: 'flex', width: tijdlijnBreedte }}>
                            {workingDays.map(day => {
                              const existingData = personDayCapacity[day.iso] || { percentage: 0, blockingType: null, hours: 0, tasks: [] };
                              const combinedData = getCombinedCapacity(person.id, role, day.iso);
                              const style = getCapacityStyle(combinedData.percentage, combinedData.blockingType);
                              const hasNewTasks = combinedData.hasNewTasks;

                              return (
                                <Tooltip
                                  key={day.iso}
                                  title={
                                    <Box>
                                      <Typography variant="caption" fontWeight={600} display="block">
                                        {day.dayName} {day.dayNum}
                                      </Typography>
                                      {existingData.tasks.length > 0 && (
                                        <>
                                          <Typography variant="caption" display="block" sx={{ opacity: 0.7, mt: 0.5 }}>Bestaand:</Typography>
                                          {existingData.tasks.map((task, i) => (
                                            <Typography key={i} variant="caption" display="block" sx={{ opacity: 0.9 }}>
                                              • {task.name} ({task.hours || 8}u)
                                            </Typography>
                                          ))}
                                        </>
                                      )}
                                      {hasNewTasks && (
                                        <>
                                          <Typography variant="caption" display="block" sx={{ opacity: 0.7, mt: 0.5, color: '#60a5fa' }}>+ Nieuw:</Typography>
                                          <Typography variant="caption" display="block" sx={{ color: '#60a5fa' }}>
                                            • Nieuwe taak ({combinedData.newHours}u)
                                          </Typography>
                                        </>
                                      )}
                                      <Typography variant="caption" display="block" sx={{ mt: 0.5, borderTop: '1px solid rgba(255,255,255,0.2)', pt: 0.5 }}>
                                        Totaal: {combinedData.hours.toFixed(1)}u / 8u = {combinedData.percentage}%
                                      </Typography>
                                    </Box>
                                  }
                                  arrow
                                >
                                  <Box
                                    sx={{
                                      width: dayWidth,
                                      minWidth: dayWidth,
                                      height: 30,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      backgroundColor: style.bg,
                                      borderRight: '1px solid #fff',
                                      cursor: 'default',
                                      // Dag waar het nieuwe werk op valt: een dun streepje onderaan.
                                      ...(hasNewTasks && {
                                        boxShadow: `inset 0 -3px 0 ${roleColors[role] || '#3b82f6'}`,
                                      }),
                                    }}
                                  >
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        fontSize: '0.6rem',
                                        fontWeight: 600,
                                        color: style.text,
                                      }}
                                    >
                                      {style.label}
                                    </Typography>
                                  </Box>
                                </Tooltip>
                              );
                            })}
                          </Box>
                        </Box>

                        {/* Expanded task list */}
                        <Collapse in={isExpanded}>
                          <Box sx={{ backgroundColor: '#fafafa', borderBottom: '1px solid #e5e7eb', width: 'max-content', minWidth: '100%' }}>
                            {personTasks.length > 0 ? (
                              groupTasksForDisplay(personTasks, workingDays).map((item, idx, arr) => {
                                // Handle grouped tasks (like Vanallesuurtje)
                                if (item.isGroup) {
                                  const groupColor = getTaskColor(item);
                                  return (
                                    <Box
                                      key={item.id}
                                      sx={{
                                        display: 'flex',
                                        borderBottom: idx < arr.length - 1 ? '1px solid #f3f4f6' : 'none',
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          width: sidebarWidth,
                                          minWidth: sidebarWidth,
                                          position: 'sticky', left: 0, zIndex: 1, backgroundColor: '#fafafa',
                                          borderRight: '1px solid #e5e7eb',
                                          p: 0.5,
                                          pl: 4,
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: 1,
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            width: 8,
                                            height: 8,
                                            borderRadius: 1,
                                            backgroundColor: groupColor.bg,
                                            flexShrink: 0,
                                          }}
                                        />
                                        <Typography
                                          variant="caption"
                                          noWrap
                                          sx={{ fontSize: '0.65rem', color: '#6b7280' }}
                                        >
                                          {item.name} ({item.tasks.length}x)
                                        </Typography>
                                      </Box>
                                      <Box sx={{ flex: 1, position: 'relative', minHeight: 24, display: 'flex' }}>
                                        {workingDays.map(day => {
                                          // Check if any task in the group is on this day
                                          const tasksOnDay = item.tasks.filter(t => {
                                            const taskDays = getTaskDaysMap(t, workingDays);
                                            return taskDays[day.iso];
                                          });
                                          const totalHours = tasksOnDay.reduce((sum, t) => sum + (t.hours || 1), 0);

                                          return (
                                            <Box
                                              key={day.iso}
                                              sx={{
                                                width: dayWidth,
                                                minWidth: dayWidth,
                                                height: 24,
                                                backgroundColor: tasksOnDay.length > 0 ? groupColor.bg : 'transparent',
                                                borderRight: '1px solid rgba(255,255,255,0.3)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                              }}
                                            >
                                              {tasksOnDay.length > 0 && (
                                                <Typography
                                                  variant="caption"
                                                  sx={{
                                                    fontSize: '0.55rem',
                                                    color: groupColor.text,
                                                    fontWeight: 500,
                                                  }}
                                                >
                                                  {totalHours}u
                                                </Typography>
                                              )}
                                            </Box>
                                          );
                                        })}
                                      </Box>
                                    </Box>
                                  );
                                }

                                // Regular task
                                const task = item;
                                const taskColor = getTaskColor(task);
                                const taskDays = getTaskDaysMap(task, workingDays);
                                const taskDayCount = telWerkdagen(task.startDate, task.endDate || task.startDate)
                                  || Object.keys(taskDays).length || 1;

                                return (
                                  <Box
                                    key={task.id || idx}
                                    sx={{
                                      display: 'flex',
                                      borderBottom: idx < arr.length - 1 ? '1px solid #f3f4f6' : 'none',
                                    }}
                                  >
                                    {/* Task name */}
                                    <Box
                                      sx={{
                                        width: sidebarWidth,
                                        minWidth: sidebarWidth,
                                        position: 'sticky', left: 0, zIndex: 1, backgroundColor: '#fafafa',
                                        borderRight: '1px solid #e5e7eb',
                                        p: 0.5,
                                        pl: 4,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          width: 8,
                                          height: 8,
                                          borderRadius: 1,
                                          backgroundColor: taskColor.bg,
                                          flexShrink: 0,
                                        }}
                                      />
                                      <Typography
                                        variant="caption"
                                        noWrap
                                        sx={{ fontSize: '0.65rem', color: '#6b7280' }}
                                        title={task.name}
                                      >
                                        {task.name}
                                      </Typography>
                                    </Box>

                                    {/* Task bar spanning days */}
                                    <Box sx={{ flex: 1, position: 'relative', minHeight: 24, display: 'flex' }}>
                                      {workingDays.map(day => {
                                        const isTaskDay = taskDays[day.iso];

                                        return (
                                          <Box
                                            key={day.iso}
                                            sx={{
                                              width: dayWidth,
                                              minWidth: dayWidth,
                                              height: 24,
                                              backgroundColor: isTaskDay ? taskColor.bg : 'transparent',
                                              borderRight: '1px solid rgba(255,255,255,0.3)',
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'center',
                                            }}
                                          >
                                            {isTaskDay && (
                                              <Typography
                                                variant="caption"
                                                sx={{
                                                  fontSize: '0.55rem',
                                                  color: taskColor.text,
                                                  fontWeight: 500,
                                                }}
                                              >
                                                {Math.round((task.hours || 8) / taskDayCount)}u
                                              </Typography>
                                            )}
                                          </Box>
                                        );
                                      })}
                                    </Box>
                                  </Box>
                                );
                              })
                            ) : (
                              <Box sx={{ p: 1, pl: 4 }}>
                                <Typography variant="caption" color="text.secondary">
                                  Geen taken in deze periode
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Collapse>
                      </Box>
                    );
                  })
                ) : (
                  <Box sx={{ p: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Geen {role.toLowerCase()}s gevonden
                    </Typography>
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>

        {/* Legenda: alleen wat een kleur heeft */}
        <Box sx={{ px: 3, py: 0.75, borderTop: '1px solid #e5e7eb', display: 'flex', gap: 2.5, flexWrap: 'wrap', alignItems: 'center' }}>
          {[
            { label: 'Draaidag', bg: getCapacityStyle(0, 'draaidag').bg },
            { label: 'Vrij', bg: getCapacityStyle(0, 'vrij').bg },
            { label: '80–100% bezet', bg: getCapacityStyle(90, null).bg },
            { label: 'Overvol', bg: getCapacityStyle(120, null).bg },
          ].map(item => (
            <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: 0.5, backgroundColor: item.bg, border: '1px solid rgba(0,0,0,0.06)' }} />
              <Typography variant="caption" color="text.secondary">{item.label}</Typography>
            </Box>
          ))}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 10, height: 10, borderBottom: '3px solid #3b82f6' }} />
            <Typography variant="caption" color="text.secondary">dag met nieuw werk</Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: '1px solid #e5e7eb' }}>
        <Button
          onClick={handleAddToCalendar}
          disabled={calendarLoading}
          startIcon={calendarLoading ? <CircularProgress size={16} /> : <CalendarMonthIcon />}
          sx={{ mr: 'auto', textTransform: 'none' }}
          size="small"
        >
          Naar Outlook
        </Button>
        {!allAssigned && (
          <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
            Kies per rol wie het doet (of &lsquo;nog te plannen&rsquo;)
          </Typography>
        )}
        <Button onClick={onClose}>Annuleren</Button>
        <Button
          variant="contained"
          onClick={handleApply}
          disabled={!allAssigned}
          startIcon={<CheckCircleIcon />}
          sx={{ borderRadius: 2 }}
        >
          Toepassen
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default PlanningPuzzleDialog;
